import type { EvaluationExam, ExamResourceType, DeliveryFormat, DeliveryProfile } from '@interactive-room';

const STORAGE_KEY = 'sala_evaluation_exams';

function readAll(): EvaluationExam[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as EvaluationExam[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(exams: EvaluationExam[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(exams));
}

export function createExamId(): string {
  return `exam_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function listEvaluationExams(): EvaluationExam[] {
  return readAll().sort((a, b) => b.updatedAt - a.updatedAt);
}

export function getEvaluationExam(id: string): EvaluationExam | null {
  return readAll().find((e) => e.id === id) ?? null;
}

export function saveEvaluationExam(exam: EvaluationExam): EvaluationExam {
  const all = readAll();
  const now = Date.now();
  const saved: EvaluationExam = {
    ...exam,
    updatedAt: now,
    createdAt: exam.createdAt || now,
  };
  const idx = all.findIndex((e) => e.id === saved.id);
  if (idx >= 0) all[idx] = saved;
  else all.push(saved);
  writeAll(all);
  return saved;
}

export function deleteEvaluationExam(id: string): void {
  writeAll(readAll().filter((e) => e.id !== id));
}

export function emptyEvaluationExam(
  teacherId = 'teacher',
  resourceType: ExamResourceType = 'exam',
  deliveryFormat?: DeliveryFormat,
  deliveryProfile?: DeliveryProfile,
): EvaluationExam {
  const now = Date.now();
  const isGuide = resourceType === 'guide';
  const isInteractive = deliveryFormat === 'interactive';
  return {
    id: createExamId(),
    teacherId,
    title: isGuide
      ? 'Nueva guía de trabajo'
      : isInteractive
        ? 'Nueva clase interactiva'
        : 'Nueva evaluación programada',
    subjectTag: 'general',
    resourceType,
    deliveryFormat: isGuide ? 'print' : deliveryFormat,
    deliveryProfile,
    createdAt: now,
    updatedAt: now,
    settings: {
      shuffleBlocks: false,
      allowRejoin: true,
      integrityMode: isInteractive ? 'FLEXIBLE' : 'STRICT',
      advanceMode: isInteractive ? 'HOST' : 'AUTONOMOUS',
    },
    blocks: [],
  };
}
